import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Clock, ChevronRight, Hammer, Ruler, Wrench, Sofa, DoorClosed, Flame, TrendingUp, Smartphone, Monitor, ChevronDown, Star, Lock } from 'lucide-react';

// --- DATA ---

const LINKS = {
  basic: "https://pay.hotmart.com/Y107388561P?off=wortdxf7&checkoutMode=10",
  complete: "https://pay.hotmart.com/Y107388561P?off=ybfjfldg&checkoutMode=10"
};
const IMAGES = {
  mainMockup: "https://cdn.magicpatterns.com/uploads/foDuGeebPb9zYqFr34AYxc/PLAN_COMPLETO-removebg-preview.png",
  basicMockup: "https://cdn.magicpatterns.com/uploads/8iKjDhnCF5zBCq2xSbKSG1/PLAN_BASICO-removebg-preview.png",
  bonuses: ["https://cdn.magicpatterns.com/uploads/4L7Df4CQan1qdCecRW3KRA/ChatGPT_Image_30_ago_2026__01_34_08-removebg-preview.png", "https://cdn.magicpatterns.com/uploads/9oQSka6oSS8sGKFMcd5Ui4/ChatGPT_Image_30_ago_2026__01_34_21-removebg-preview.png", "https://cdn.magicpatterns.com/uploads/vAQeJQBwTZ2eBCF6PMeSwV/ChatGPT_Image_30_ago_2026__01_34_29-removebg-preview.png", "https://cdn.magicpatterns.com/uploads/j7J5tWntSkoijDQyAKtKxE/ChatGPT_Image_30_ago_2026__01_34_36-removebg-preview.png", "https://cdn.magicpatterns.com/uploads/oCT8CrJ1iU1kSCW4eencGa/ChatGPT_Image_30_ago_2026__01_34_43-removebg-preview.png", "https://cdn.magicpatterns.com/uploads/sWKdo1adNNT1hD9AsHuard/ChatGPT_Image_30_ago_2026__01_34_51-removebg-preview.png", "https://cdn.magicpatterns.com/uploads/kFUum5VmihfP2MzkTTH9an/ChatGPT_Image_30_ago_2026__01_34_56-removebg-preview.png"]
};
const FAQS = [{
  question: "¿Para quién es El Gran Manual del Herrero?",
  answer: "Es ideal tanto para principiantes que quieren aprender desde cero y fabricar sus primeros proyectos, como para herreros experimentados que buscan nuevos diseños, planos exactos y formas de optimizar su tiempo en el taller."
}, {
  question: "¿Necesito herramientas costosas para empezar?",
  answer: "No. Muchos de los proyectos están diseñados para ser fabricados con herramientas básicas de taller (soldadora, amoladora, taladro, cinta métrica). A medida que avances, podrás incorporar más equipos."
}, {
  question: "¿Cómo recibo el acceso al material?",
  answer: "Inmediatamente después de que se confirme tu pago, recibirás un correo electrónico con tus datos de acceso a nuestra plataforma exclusiva donde podrás ver y descargar todo el material."
}, {
  question: "¿Puedo acceder desde el celular?",
  answer: "Sí, la plataforma y los documentos están optimizados para que puedas consultarlos desde tu celular, tablet o computadora, ideal para tenerlos a mano en el taller."
}, {
  question: "¿El acceso es por tiempo limitado?",
  answer: "No, tu acceso al Plan Completo o Básico es de por vida. Podrás consultar el material siempre que lo necesites."
}, {
  question: "¿Cómo funciona la garantía?",
  answer: "Tienes 7 días de garantía incondicional. Si revisas el material y sientes que no es lo que esperabas, puedes solicitar el reembolso completo de tu dinero desde la plataforma, sin preguntas."
}];

// --- COMPONENTS ---

const CTAButton = ({
  href,
  text,
  className = ""
}: {
  href: string;
  text: string;
  className?: string;
}) => <a href={href} className={`inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-bold py-4 px-8 rounded-xl text-lg md:text-xl transition-all transform hover:scale-105 shadow-[0_0_20px_rgba(234,88,12,0.4)] ${className}`} data-id="element-0" data-mp="13fg9xmb736w4">
    {text}
    <ChevronRight className="w-6 h-6" data-id="element-1" data-mp="1i2j87v7cdjki" />
  </a>;
const SectionHeading = ({
  children,
  subtitle
}: {
  children: React.ReactNode;
  subtitle?: string;
}) => <div className="text-center mb-12" data-id="element-2" data-mp="1m70m5dr0t2nd">
    <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight" data-id="element-3" data-mp="6o5uvka7smiq">
      {children}
    </h2>
    {subtitle && <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto" data-id="element-4" data-mp="arc32xgw0nru">
        {subtitle}
      </p>}
  </div>;

// --- MAIN APP ---

export function App() {
  useEffect(() => {
    document.title = "El Gran Manual del Herrero";
    const script = document.createElement('script');
    script.innerHTML = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1574961557675508');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=1574961557675508&ev=PageView&noscript=1" />`;
    document.head.appendChild(noscript);
  }, []);
  return <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-orange-500/30" data-id="element-5" data-mp="205ihl5jvszm6">
      
      {/* TOP BANNER */}
      <div className="bg-orange-600 text-white py-2 px-4 text-center text-sm md:text-base font-medium flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6" data-id="element-6" data-mp="254blq7da3e9o">
        <span className="flex items-center gap-2" data-id="element-7" data-mp="2evpqouo016a7"><Clock className="w-4 h-4" data-id="element-8" data-mp="2eun1qsa8ldwx" /> OFERTA ESPECIAL POR TIEMPO LIMITADO</span>
        <span className="hidden md:inline" data-id="element-9" data-mp="2jsoiv7o3r35r">•</span>
        <span className="flex items-center gap-2" data-id="element-10" data-mp="2nvl65bol7ng1"><ShieldCheck className="w-4 h-4" data-id="element-11" data-mp="2nwny7mxnbvxj" /> COMPRA 100% SEGURA Y PROTEGIDA</span>
      </div>

      {/* HERO SECTION */}
      <header className="relative pt-16 pb-20 md:pt-24 md:pb-32 px-4 overflow-hidden" data-id="element-12" data-mp="2su5zcr5f96uz">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-800 via-slate-950 to-slate-950 -z-10" data-id="element-13" data-mp="tivaaoens460"></div>
        
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-14" data-mp="21r2bs5foqlqn">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="space-y-6" data-id="element-15" data-mp="1wsr0dq9qs36b">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 text-orange-400 px-4 py-1.5 rounded-full text-sm font-semibold tracking-wide" data-id="element-16" data-mp="25tfgcqip4dlu">
              MÁS DE 600 PROYECTOS LISTOS
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-tight" data-id="element-17" data-mp="2fs4wadr17nzg">
              El Gran Manual <br data-id="element-18" data-mp="2thoxhf4ysd6n" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600" data-id="element-19" data-mp="2ti8i53m01fod">
                del Herrero
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed" data-id="element-20" data-mp="33hzc946ndsnx">
              Deja de buscar planos incompletos en internet. Accede a la biblioteca más grande de proyectos de herrería con medidas exactas, lista de materiales y paso a paso.
            </p>
            
            <div className="pt-4" data-id="element-21" data-mp="3n399mysoidfl">
              <CTAButton href={LINKS.complete} text="QUIERO ACCEDER AL MANUAL" className="w-full md:w-auto" data-id="element-22" data-mp="3n27mhvye20hi" />
              <p className="text-sm text-slate-500 mt-4 flex items-center justify-center md:justify-start gap-2" data-id="element-23" data-mp="38576gppk7tyv">
                <Smartphone className="w-4 h-4" data-id="element-24" data-mp="3d6cz6nj7zh8e" /> Acceso inmediato desde celular, tablet o PC.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative" data-id="element-25" data-mp="344wyx8m4cvhw">
            <div className="absolute inset-0 bg-orange-500/20 blur-[100px] rounded-full" data-id="element-26" data-mp="2uek48nklbly1"></div>
            <img src={IMAGES.mainMockup} alt="El Gran Manual del Herrero Paquete Completo" className="relative z-10 w-full max-w-lg mx-auto drop-shadow-2xl" data-id="element-27" data-mp="2geswv5okx3t0" />
          </motion.div>
        </div>
      </header>

      {/* PROJECT GALLERY */}
      <section className="py-16 px-4 bg-slate-950" data-id="element-28" data-mp="2vxls5fisjd0e">
        <div className="max-w-6xl mx-auto text-center" data-id="element-29" data-mp="2qwxs64cl3lu1">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-8" data-id="element-30" data-mp="2qvmr4caiws6m">
            Cientos de ideas listas para fabricar
          </h2>
          <div className="rounded-2xl overflow-hidden border border-slate-800 shadow-2xl" data-id="element-31" data-mp="2hwj7963j88r2">
            <img src="https://cdn.magicpatterns.com/uploads/xwwdaUnyi627YpMVFVSQVU/ChatGPT_Image_29_ago_2026,_19_40_53.png" alt="Galería de proyectos de herrería" className="w-full h-auto" data-id="element-32" data-mp="2d5sz5l3q1rso" />
          </div>
        </div>
      </section>

      {/* PROBLEM / CATEGORIES */}
      <section className="py-20 px-4 bg-slate-900 border-y border-slate-800" data-id="element-33" data-mp="1y8s69e9snqsm">
        <div className="max-w-6xl mx-auto" data-id="element-34" data-mp="1u76y2687g0or">
          <SectionHeading subtitle="Encuentra todo organizado en un solo material, listo para fabricar." data-id="element-35" data-mp="1u8894ap80n6h">
            ¿Qué encontrarás en el Manual?
          </SectionHeading>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-36" data-mp="dpq214sbvlew">
            {[{
            icon: Sofa,
            title: "Muebles Industriales",
            desc: "Mesas, sillas, estanterías y escritorios combinando hierro y madera."
          }, {
            icon: DoorClosed,
            title: "Portones y Rejas",
            desc: "Diseños seguros y estéticos para frentes, ventanas y puertas."
          }, {
            icon: TrendingUp,
            title: "Escaleras y Barandas",
            desc: "Estructuras sólidas, rectas y de caracol con medidas precisas."
          }, {
            icon: Hammer,
            title: "Estructuras Metálicas",
            desc: "Tinglados, techos y soportes de alta resistencia."
          }, {
            icon: Flame,
            title: "Asadores y Parrillas",
            desc: "Diferentes modelos de parrillas, fogoneros y accesorios."
          }, {
            icon: Wrench,
            title: "Herramientas Caseras",
            desc: "Fabrica tus propias herramientas y prensas para el taller."
          }].map((item, i) => <div key={i} className="bg-slate-950 p-6 rounded-2xl border border-slate-800 hover:border-orange-500/50 transition-colors group" data-id="element-37" data-mp="2f6lpdcu5l4wy" data-mpi={i}>
                <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center text-orange-500 mb-4 group-hover:bg-orange-500 group-hover:text-white transition-colors" data-id="element-38" data-mp="2p5b6v4i50nco">
                  <item.icon className="w-6 h-6" data-id="element-39" data-mp="1wgniv1607jvf" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-40" data-mp="1wigpiggma43f">{item.title}</h3>
                <p className="text-slate-400" data-id="element-41" data-mp="21gi68uqklrbq">{item.desc}</p>
              </div>)}
          </div>

          <div className="text-center mt-12" data-id="element-42" data-mp="26d8ljfujwiw3">
            <CTAButton href={LINKS.complete} text="VER TODOS LOS PROYECTOS" data-id="element-43" data-mp="2b59yz53ggfnp" />
          </div>
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="py-20 px-4" data-id="element-44" data-mp="1dpw1pdfryp6h">
        <div className="max-w-6xl mx-auto" data-id="element-45" data-mp="1ioz74yzo2xez">
          <SectionHeading subtitle="Diseñado para que no pierdas tiempo y vayas directo a la acción." data-id="element-46" data-mp="1inxsylnctut1">
            ¿Qué hace tan valioso a este Manual?
          </SectionHeading>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-47" data-mp="3ryyl8wv2mu1q">
            {[{
            title: "Planos y Medidas Exactas",
            desc: "Se acabó el adivinar. Cada proyecto incluye las medidas precisas para que cortes y sueldes con seguridad."
          }, {
            title: "Lista de Materiales",
            desc: "Conoce exactamente qué perfiles, tubos y accesorios necesitas comprar antes de empezar."
          }, {
            title: "Todo Organizado en un Solo Lugar",
            desc: "Deja de buscar videos sueltos o imágenes borrosas. Ten tu biblioteca de consulta siempre a mano."
          }, {
            title: "Aumenta tus Ganancias",
            desc: "Ofrece un catálogo más amplio a tus clientes con diseños probados y rentables."
          }].map((item, i) => <div key={i} className="flex gap-4 items-start" data-id="element-48" data-mp="2u9n5u6qsr7pq" data-mpi={i}>
                <div className="mt-1" data-id="element-49" data-mp="2pak1ypmjz7jc">
                  <CheckCircle2 className="w-6 h-6 text-orange-500" data-id="element-50" data-mp="2pblkff5wewb3" />
                </div>
                <div data-id="element-51" data-mp="2flv636dc1fqr">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-52" data-mp="2fpj2qee2qi9p">{item.title}</h3>
                  <p className="text-slate-400" data-id="element-53" data-mp="2knub0koq0kpt">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* VIDEO SHOWCASE */}
      <section className="py-20 px-4 bg-slate-950 border-t border-slate-800" data-id="element-54" data-mp="2aovaxqi0txef">
        <div className="max-w-6xl mx-auto" data-id="element-55" data-mp="2yez1oxa422o0">
          <SectionHeading subtitle="Observa un poco de lo que encontrarás dentro del manual." data-id="element-56" data-mp="2yg0nnwso09ki">
            Mira el Manual por Dentro
          </SectionHeading>
          
          <div className="grid md:grid-cols-2 gap-8" data-id="element-57" data-mp="2pl48mnm9k2oq">
            <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-xl" data-id="element-58" data-mp="2tmz95pwdec1g">
              <video src="https://cdn.magicpatterns.com/uploads/uchfKKg7sbiXUwoJJTufDC/3d5772d6-6f34-46af-bf90-a458b62a9f90.mp4" controls muted autoPlay loop playsInline className="w-full aspect-[9/16] md:aspect-video object-cover" data-id="element-59" data-mp="3hca24l7hb251" />
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-900 shadow-xl" data-id="element-60" data-mp="1kecq3ig6ozv5">
              <video src="https://cdn.magicpatterns.com/uploads/4f6nTokCty9mVBwvRXdKJQ/58cdb96a46734ee7b8eed1850976ceb4.mp4" controls muted autoPlay loop playsInline className="w-full aspect-[9/16] md:aspect-video object-cover" data-id="element-61" data-mp="1ffjc7owzj6ug" />
            </div>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-20 px-4 bg-slate-900 border-y border-slate-800" data-id="element-62" data-mp="wn70wcocsv86">
        <div className="max-w-6xl mx-auto" data-id="element-63" data-mp="2d1yuuu15eajl">
          <div className="text-center mb-16" data-id="element-64" data-mp="2d54gu0ehcwb3">
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4" data-id="element-65" data-mp="284q96jgsjodl">
              ¿Y Eso No Es Todo?
            </h2>
            <p className="text-xl text-orange-400 font-medium" data-id="element-66" data-mp="hoecfc7nh3dq">
              Llevando el PLAN COMPLETO hoy, recibes 7 BONOS EXCLUSIVOS
            </p>
          </div>

          <div className="space-y-12" data-id="element-67" data-mp="qn8bd98ge5ug">
            {[{
            img: IMAGES.bonuses[0],
            title: "BONO #1: Proyectos de Alto Rendimiento",
            desc: "Planos detallados para fabricar Trailers y equipamiento completo de Gimnasio. Proyectos de alta demanda y excelente rentabilidad."
          }, {
            img: IMAGES.bonuses[1],
            title: "BONO #2: Soldar Desde Cero",
            desc: "Guía completa paso a paso para dominar la soldadura. Aprende técnicas esenciales para lograr uniones fuertes y duraderas."
          }, {
            img: IMAGES.bonuses[2],
            title: "BONO #3: Presupuestos y Ventas",
            desc: "Calcula, cotiza y vende como un profesional. Plantillas para calcular materiales, mano de obra y asegurar tu margen de ganancia."
          }, {
            img: IMAGES.bonuses[3],
            title: "BONO #4: Aprende Tornería Desde Cero",
            desc: "De principiante a experto. Domina el torno con técnicas fundamentales, prácticas reales y guías paso a paso."
          }, {
            img: IMAGES.bonuses[4],
            title: "BONO #5: Herrería Artística",
            desc: "Técnicas que multiplican tu precio. Diseños decorativos, acabados de alto nivel y creación de piezas únicas de gran valor."
          }, {
            img: IMAGES.bonuses[5],
            title: "BONO #6: Maestro en Acción (Videos)",
            desc: "Proyectos explicados paso a paso en video. Observa técnicas de experto, consejos y trucos aplicados en tiempo real."
          }, {
            img: IMAGES.bonuses[6],
            title: "BONO #7: Carpintería Que Vende",
            desc: "+100 proyectos de madera con alta demanda. Combina herrería y carpintería para crear muebles estilo industrial que sí se venden."
          }].map((bono, i) => <div key={i} className="flex flex-col md:flex-row gap-8 items-center bg-slate-950 p-6 md:p-8 rounded-3xl border border-slate-800" data-id="element-68" data-mp="2zx7rfcnf4cju" data-mpi={i}>
                <div className="w-full md:w-1/3 flex-shrink-0" data-id="element-69" data-mp="2v3d1vsl4t3cu">
                  <img src={bono.img} alt={bono.title} className="w-full rounded-xl drop-shadow-lg" data-id="element-70" data-mp="t98fmkuer3f7" />
                </div>
                <div className="w-full md:w-2/3" data-id="element-71" data-mp="y68qra646wd6">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-72" data-mp="jivzd4tmlwev">{bono.title}</h3>
                  <p className="text-lg text-slate-300" data-id="element-73" data-mp="ogp6q16o0wgq">{bono.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="py-24 px-4 relative overflow-hidden" data-id="element-74" data-mp="fh23rayafilk">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-orange-600/10 blur-[120px] rounded-full -z-10" data-id="element-75" data-mp="fe64njapenvj"></div>
        
        <div className="max-w-5xl mx-auto" data-id="element-76" data-mp="5iuwxkxpqgxa">
          <SectionHeading subtitle="Elige la opción ideal para ti y comienza a fabricar hoy mismo." data-id="element-77" data-mp="1uxiy0q0uscg0">
            ÚLTIMA OPORTUNIDAD: APROVECHA ESTA CONDICIÓN ESPECIAL
          </SectionHeading>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-end" data-id="element-78" data-mp="3keek4pszj09u">
            
            {/* BASIC PLAN */}
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 flex flex-col h-full relative" data-id="element-79" data-mp="3fg39icuv8lqk">
              <h3 className="text-2xl font-bold text-white mb-2" data-id="element-80" data-mp="3u4zq4yazn72s">PLAN BÁSICO</h3>
              <p className="text-slate-400 mb-6 min-h-[48px]" data-id="element-81" data-mp="3pd7wgewjcl8f">Ideal para quien solo busca la referencia visual y los planos básicos.</p>
              
              <div className="mb-6 flex justify-center" data-id="element-82" data-mp="71l5qnbyjscr">
                <img src={IMAGES.basicMockup} alt="Plan Básico" className="h-48 object-contain drop-shadow-md" data-id="element-83" data-mp="2wvbt40v0nuh" />
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-84" data-mp="gwwln8z3r5m3">
                <li className="flex items-start gap-3" data-id="element-85" data-mp="bybndf3euw0a"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-86" data-mp="bzeadbxm4eax" /> <span data-id="element-87" data-mp="2io9gt6b9pz4y">El Gran Manual del Herrero (+600 proyectos)</span></li>
                <li className="flex items-start gap-3" data-id="element-88" data-mp="2dq7xqldudzth"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-89" data-mp="2dr9jbjshi4pg" /> <span data-id="element-90" data-mp="1rx26ps0sf9l2">Planos y Medidas</span></li>
                <li className="flex items-start gap-3" data-id="element-91" data-mp="1rw985ebzfqe0"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-92" data-mp="1wvvlrovq38nc" /> <span data-id="element-93" data-mp="1wuat64syxzs4">Acceso de por vida</span></li>
                <li className="flex items-start gap-3 opacity-40" data-id="element-94" data-mp="21m297lei0n40"><CheckCircle2 className="w-5 h-5 shrink-0" data-id="element-95" data-mp="21ndp2jl7xlbh" /> <span data-id="element-96" data-mp="26lf6l02zxcku">Sin Bonos Adicionales</span></li>
              </ul>

              <div className="text-center mb-6" data-id="element-97" data-mp="26nj9bep5s5jj">
                <p className="text-slate-500 line-through text-lg" data-id="element-98" data-mp="18yzqo2q7wmze">Antes $29.90 USD</p>
                <p className="text-4xl font-extrabold text-white" data-id="element-99" data-mp="1dw7xj75x14lc">
                  $6.90 <span className="text-xl text-slate-400 font-normal" data-id="element-100" data-mp="1iw449e5zza4l">USD</span>
                </p>
                <p className="text-sm text-slate-400 mt-2" data-id="element-101" data-mp="1nv79ozpw3id3">Pago único · Acceso inmediato</p>
                <p className="text-xs text-orange-400/80 mt-1" data-id="element-102" data-mp="1nsukq2lps7qj">* El monto se reflejará en tu moneda local </p>
              </div>

              <a href={LINKS.basic} className="block w-full py-4 text-center rounded-xl font-bold text-white bg-slate-800 hover:bg-slate-700 transition-colors border border-slate-700" data-id="element-103" data-mp="2z9ksvr9p79ms">
                QUIERO EL PLAN BÁSICO
              </a>
            </div>

            {/* COMPLETE PLAN */}
            <div className="bg-gradient-to-b from-slate-800 to-slate-900 border-2 border-orange-500 rounded-3xl p-8 flex flex-col h-full relative transform md:-translate-y-4 shadow-[0_0_40px_rgba(234,88,12,0.15)]" data-id="element-104" data-mp="dvcwfj3ar01q">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1 shadow-lg" data-id="element-105" data-mp="3wx63ic3futa">
                <Star className="w-4 h-4 fill-current" data-id="element-106" data-mp="3r496w76bhmzi" /> MÁS ELEGIDO
              </div>

              <h3 className="text-2xl font-bold text-white mb-2 text-center" data-id="element-107" data-mp="3m5xx1wg0vcha">PLAN COMPLETO</h3>
              <p className="text-orange-200 mb-6 text-center min-h-[48px]" data-id="element-108" data-mp="1pb1jeagjvz2j">La opción ideal para tener el material completo y todos los bonos.</p>
              
              <div className="mb-6 flex justify-center" data-id="element-109" data-mp="2c72tavfw0b7e">
                <img src={IMAGES.mainMockup} alt="Plan Completo" className="h-48 object-contain drop-shadow-xl" data-id="element-110" data-mp="2c6knp87c4kmt" />
              </div>

              <ul className="space-y-3 mb-8 flex-grow" data-id="element-111" data-mp="22hj83j4pc59d">
                <li className="flex items-start gap-3" data-id="element-112" data-mp="22fyfhz1y6we5"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-113" data-mp="1xig3fzsmw11x" /> <span className="font-medium text-white" data-id="element-114" data-mp="1xhwuh8kyon01">El Gran Manual del Herrero (+600 proyectos)</span></li>
                <li className="flex items-start gap-3" data-id="element-115" data-mp="1ti5uce9jouiu"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-116" data-mp="1oi1duo8mzzsm" /> <span data-id="element-117" data-mp="1oggmt8lj6yzi">Planos, Medidas y Lista de Materiales</span></li>
                <li className="flex items-start gap-3" data-id="element-118" data-mp="1jjge0pxari4p"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-119" data-mp="1jki1xuzevz3u" /> <span className="text-orange-300" data-id="element-120" data-mp="1ekvnxjbreetz">🎁 Bono #1: Trailers y Gym</span></li>
                <li className="flex items-start gap-3" data-id="element-121" data-mp="3dvn0wlzjzncu"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-122" data-mp="3dulpggemkyul" /> <span className="text-orange-300" data-id="element-123" data-mp="38yo7y6rmlmg8">🎁 Bono #2: Soldar Desde Cero</span></li>
                <li className="flex items-start gap-3" data-id="element-124" data-mp="3hff2vg92oeu3"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-125" data-mp="3hedgifmlw5x2" /> <span className="text-orange-300" data-id="element-126" data-mp="3lffgiyockm03">🎁 Bono #3: Presupuestos y Ventas</span></li>
                <li className="flex items-start gap-3" data-id="element-127" data-mp="3q6nr3y1eydeu"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-128" data-mp="3q7pft5bcqyf1" /> <span className="text-orange-300" data-id="element-129" data-mp="3v6a3y6dbxrn4">🎁 Bono #4: Tornería Desde Cero</span></li>
                <li className="flex items-start gap-3" data-id="element-130" data-mp="2yg9h4cwitl8g"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-131" data-mp="2yhcb4tp543sl" /> <span className="text-orange-300" data-id="element-132" data-mp="32gtg7m0nmyx8">🎁 Bono #5: Herrería Artística</span></li>
                <li className="flex items-start gap-3" data-id="element-133" data-mp="37fwmf9sdfb6s"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-134" data-mp="37fo595p7tjps" /> <span className="text-orange-300" data-id="element-135" data-mp="3cg25w2ohlrds">🎁 Bono #6: Maestro en Acción (Videos)</span></li>
                <li className="flex items-start gap-3" data-id="element-136" data-mp="mqqelifs4tdc"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-137" data-mp="mpp3xf2oe8w5" /> <span className="text-orange-300" data-id="element-138" data-mp="ros8yzinof44">🎁 Bono #7: Carpintería Que Vende</span></li>
                <li className="flex items-start gap-3" data-id="element-139" data-mp="20wd77uyy9nl"><CheckCircle2 className="w-5 h-5 text-orange-500 shrink-0" data-id="element-140" data-mp="1zv0z027vh4a" /> <span data-id="element-141" data-mp="3t7z0vpbllxt2">Acceso de por vida + Actualizaciones</span></li>
              </ul>

              <div className="text-center mb-6" data-id="element-142" data-mp="bz2afc5tyrm4">
                <p className="text-slate-400 line-through text-lg" data-id="element-143" data-mp="bytwdgxz1g9c">Antes $59.90 USD</p>
                <p className="text-5xl font-extrabold text-white" data-id="element-144" data-mp="71agydpjexft">
                  $13.90 <span className="text-xl text-slate-300 font-normal" data-id="element-145" data-mp="3ffj6te0oda4a">USD</span>
                </p>
                <p className="text-sm text-orange-200 mt-2 font-medium" data-id="element-146" data-mp="3ahhl0lbv6wp4">Pago único · Acceso inmediato</p>
                <p className="text-xs text-orange-300/90 mt-1" data-id="element-147" data-mp="3p640cn1hrogc">* El monto se reflejará en tu moneda local</p>
              </div>

              <CTAButton href={LINKS.complete} text="QUIERO EL MATERIAL COMPLETO" className="w-full text-lg py-5" data-id="element-148" data-mp="3kdkopvmd73e6" />
              <p className="text-xs text-center text-slate-400 mt-4 flex items-center justify-center gap-1" data-id="element-149" data-mp="2wnq9j7ekp2qr">
                <Lock className="w-3 h-3" data-id="element-150" data-mp="2roni4primv19" /> Compra 100% segura
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 px-4 bg-slate-900 border-y border-slate-800" data-id="element-151" data-mp="1bzxoe0tjsqn5">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 text-center md:text-left" data-id="element-152" data-mp="xb18xip5wbci">
          <div className="w-32 h-32 shrink-0 bg-orange-500/10 rounded-full flex items-center justify-center border border-orange-500/30" data-id="element-153" data-mp="12a4d70xbidjf">
            <ShieldCheck className="w-16 h-16 text-orange-500" data-id="element-154" data-mp="1px457pupof9q" />
          </div>
          <div data-id="element-155" data-mp="1ux8lpfvmd9zy">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-156" data-mp="1uvnt3vsv814q">GARANTÍA INCONDICIONAL DE 7 DÍAS</h2>
            <p className="text-slate-300 text-lg mb-4" data-id="element-157" data-mp="1kzu4vf5saj74">
              Tendrás 7 días para explorar todo el contenido del Gran Manual del Herrero, consultar los planos y utilizar los bonos.
            </p>
            <p className="text-slate-400" data-id="element-158" data-mp="9kfbelcsa9gr">
              Si dentro de ese período consideras que el material no aporta valor a tu trabajo, solo tienes que solicitar el reembolso. Recibes el 100% de tu dinero de vuelta, sin trámites y sin preguntas.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4" data-id="element-159" data-mp="g31urdqy3qr3">
        <div className="max-w-5xl mx-auto" data-id="element-160" data-mp="fzxidt3btopc">
          <SectionHeading data-id="element-161" data-mp="b3p0w91npjf0">¿Cómo funciona el acceso?</SectionHeading>
          
          <div className="grid md:grid-cols-4 gap-8 relative" data-id="element-162" data-mp="18qflgu41vq2r">
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-800 -translate-y-1/2 z-0" data-id="element-163" data-mp="18olcme61zof1"></div>
            
            {[{
            step: "1",
            title: "Realiza tu compra",
            desc: "Finaliza tu pedido en un entorno 100% seguro."
          }, {
            step: "2",
            title: "Recibe tu acceso",
            desc: "Te enviamos un email con tus datos de acceso inmediato."
          }, {
            step: "3",
            title: "Explora el contenido",
            desc: "Ingresa a la plataforma desde cualquier dispositivo."
          }, {
            step: "4",
            title: "Empieza a fabricar",
            desc: "Elige un proyecto, revisa los planos y manos a la obra."
          }].map((item, i) => <div key={i} className="relative z-10 flex flex-col items-center text-center" data-id="element-164" data-mp="1z1q3uij1ovey" data-mpi={i}>
                <div className="w-12 h-12 rounded-full bg-orange-600 text-white font-bold text-xl flex items-center justify-center mb-4 shadow-lg shadow-orange-600/30" data-id="element-165" data-mp="23zjb7ew33vgt">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-166" data-mp="2dp2rqdwbrd8i">{item.title}</h3>
                <p className="text-sm text-slate-400" data-id="element-167" data-mp="2inmgsn67t553">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-slate-900 border-t border-slate-800" data-id="element-168" data-mp="2rpdmhs0r9byy">
        <div className="max-w-3xl mx-auto" data-id="element-169" data-mp="2rn9eoyzqjste">
          <SectionHeading data-id="element-170" data-mp="sd1devb00cg4">Preguntas Frecuentes</SectionHeading>
          
          <div className="space-y-4" data-id="element-171" data-mp="xb2ujaov61oy">
            {FAQS.map((faq, i) => <FAQItem key={i} question={faq.question} answer={faq.answer} data-id="element-172" data-mp="xdfcvoyjae2j" data-mpi={i} />)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-4 text-center border-t border-slate-800 bg-slate-950" data-id="element-173" data-mp="1vo716l90vhmj">
        <div className="max-w-4xl mx-auto" data-id="element-174" data-mp="29n6ptswjbwip">
          <p className="text-slate-500 text-sm mb-4" data-id="element-175" data-mp="29ktywq8subti">
            Copyright © {new Date().getFullYear()} | Todos los derechos reservados.
          </p>
          <p className="text-slate-600 text-xs leading-relaxed" data-id="element-176" data-mp="2jkb1uzwo2bls">
            Todos los derechos sobre la obra "El Gran Manual del Herrero" están reservados. La reproducción no autorizada de esta publicación, total o parcial, por cualquier medio, constituye una violación de los derechos de autor y sujeta a los infractores a las sanciones civiles y penales previstas en la legislación aplicable. Este sitio no es parte del sitio web de Facebook o Facebook Inc. Además, este sitio NO está respaldado por Facebook de ninguna manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

    </div>;
}

// --- SUBCOMPONENTS ---

function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-slate-800 rounded-xl overflow-hidden bg-slate-950" data-id="element-177" data-mp="36z3ckp2fsuts">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-slate-900 transition-colors" data-id="element-178" data-mp="3byol9r7eshjp">
        <span className="font-bold text-white pr-4" data-id="element-179" data-mp="32y9q804n7o30">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-180" data-mp="330mhx507dct9" />
      </button>
      <AnimatePresence data-id="element-181" data-mp="2t8gpxv9yave2">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-182" data-mp="2t9hz1u7ep7t5">
            <div className="px-6 pb-4 text-slate-400" data-id="element-183" data-mp="2jcn41lacdpjm">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}